import React, { useCallback, useState } from 'react';
import cx from 'classnames';

import { Analytics } from '@trello/atlassian-analytics';
import { ErrorBoundary } from '@trello/error-reporting';
import { featureFlagClient } from '@trello/feature-flag-client';
import {
  SmartCardWrapper,
  useTrelloSmartLinkAnalytics,
} from '@trello/smart-card';
import type { SmartCardClientResponse } from '@trello/smart-card/smart-card-client';
import { smartCardClient } from '@trello/smart-card/smart-card-client';
import { CardFrontTestIds } from '@trello/test-ids';

import { DragSort } from 'app/scripts/views/lib/DragSort';
import { trelloLinkHandler } from 'app/src/trelloLinkHandler';
import { EditCardButton, useEditCardButton } from './EditCardButton';

import styles from './LinkCard.less';

interface LinkCardProps {
  url: string;
  openEditor: () => void;
  hideCover?: boolean;
  locale?: string;
  isEditable: boolean;
  hideActions?: boolean;
  analyticsContainers?: Parameters<
    typeof Analytics.sendClickedLinkEvent
  >[0]['containers'];
}

export const LinkCard = ({
  url,
  openEditor,
  hideCover,
  isEditable,
  hideActions,
  analyticsContainers,
}: LinkCardProps) => {
  const {
    showEditCardButton,
    hideEditCardButton,
    shouldShowEditCardButton,
  } = useEditCardButton();

  const [isSmartCard, setIsSmartCard] = useState(smartCardClient.isCached(url));
  const [resolvedUrl, setResolvedUrl] = useState<
    SmartCardClientResponse | undefined
  >();
  // We also looked into using `useSmartLinkAnalytics` from
  // `@atlaskit/smart-card`, but it didn't autopopulate fields like
  // `extensionKey` that we needed for our `linkCard` analytics event.
  const smartLinkAnalytics = useTrelloSmartLinkAnalytics(resolvedUrl, {
    source: 'cardView',
    containers: analyticsContainers,
    attributes: {
      component: 'linkCard',
    },
  });

  /**
   * Trello Smart Link requests are controlled by another flag
   * aaaa.web.resolve-trello-smart-links-in-trello must be enabled before
   * enabling this flag
   */
  const shouldResolveTrelloCardUrl = featureFlagClient.get(
    'aaaa.web.resolve-trello-link-cards',
    false,
  );

  const handleLinkUnresolved = useCallback(() => {
    setResolvedUrl(undefined);
    setIsSmartCard(false);
  }, []);

  const handleLinkResolved = useCallback(
    (resolved: SmartCardClientResponse) => {
      if (
        resolved.meta.key === 'trello-object-provider' &&
        !shouldResolveTrelloCardUrl
      ) {
        return handleLinkUnresolved();
      }

      setResolvedUrl(resolved);
      setIsSmartCard(resolved.meta.visibility !== 'not_found');
    },
    [handleLinkUnresolved, shouldResolveTrelloCardUrl],
  );

  const handleSmartCardClick = useCallback(() => {
    // @atlaskit/smart-card sends its own `smartLink` analytics event when
    // the user clicks the inner link text. However, since we have a wrapper
    // <a> around the whole smart card, we need to send our own event.
    // We are allowing a UGCString type within smartLinkAnalytics to get passed into this
    // analytics function because there is redaction logic in useTrelloSmartLinkAnalytics.
    Analytics.sendClickedLinkEvent({
      linkName: 'linkCard',
      ...smartLinkAnalytics,
    });
  }, [smartLinkAnalytics]);

  const plainLink = useCallback(
    () => (
      <a
        className={styles.link}
        href={url}
        target="_blank"
        onClick={handleSmartCardClick}
      >
        {url}
      </a>
    ),
    [url, handleSmartCardClick],
  );

  const getInnerContent = () => {
    // TODO: enable FlexibleBlockCard, which supports `ui.clickableContainer`
    //
    // Currently, our implementation results in nested <a> elements, which is
    // invalid and inaccessible html. We also have to send our own analytics
    // events for `linkCard` rather than relying on the `smartLink` event that
    // @atlaskit/smart-card fires from the inner <a> element.
    return (
      <a
        className={styles.link}
        href={url}
        target="_blank"
        // eslint-disable-next-line react/jsx-no-bind
        onClick={(e) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if ((DragSort as any).sorting) {
            e.stopPropagation();
            e.preventDefault();
            return;
          }
          e.stopPropagation();
          handleSmartCardClick();
        }}
      >
        <SmartCardWrapper
          smartCardProps={{
            appearance: 'block',
            inheritDimensions: true,
            showActions: !hideActions,
          }}
          smartCardProviderFeatureFlags={{
            enableFlexibleBlockCard: false,
          }}
          onLinkResolved={handleLinkResolved}
          onLinkUnresolved={handleLinkUnresolved}
          shouldRender={isSmartCard}
          onSmartCardClick={handleSmartCardClick}
          handleTrelloLinkClick={trelloLinkHandler}
          analyticsContextData={smartLinkAnalytics}
          plainLink={plainLink}
          url={url}
        />
      </a>
    );
  };

  return (
    <div
      className={cx({
        [styles.smartLinkCard]: isSmartCard,
        [styles.linkCard]: !isSmartCard,
        [styles.hideCover]: hideCover,
      })}
      data-testid={CardFrontTestIds.LinkCard}
      onFocus={showEditCardButton}
      onBlur={hideEditCardButton}
      onMouseOver={showEditCardButton}
      onMouseOut={hideEditCardButton}
    >
      <EditCardButton
        onClick={openEditor}
        shouldShow={isEditable && shouldShowEditCardButton}
      />
      <ErrorBoundary
        tags={{
          ownershipArea: 'trello-aaaa',
          feature: 'Link Card',
        }}
        sendCrashEvent={false}
        errorHandlerComponent={plainLink}
      >
        {getInnerContent()}
      </ErrorBoundary>
    </div>
  );
};
